import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { Container, Nav } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Point } from '../atom/Point'
import { motion } from 'framer-motion'

const slideDown = keyframes`
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 300px;
    opacity: 1;
  }
`

const slideUp = keyframes`
  from {
    max-height: 300px;
    opacity: 1;
  }
  to {
    max-height: 0;
    opacity: 0;
  }
`

const MenuWrapper = styled.div`
  position: relative;
  background-color: white;
  border-top: 0 !important;
  border: 1px solid #6c819c;
  z-index: 10;

  display: ${(props) => (props.isVisible ? 'block' : 'none')};
  animation: ${(props) => (props.show ? slideDown : slideUp)} 0.4s ease forwards;
`

const P = styled.p`
  font-size: 20px;
  margin-top: 30px;
  @media (max-width: 991px) {
    font-size: 16px;
    margin-top: 20px;
  }
  @media (max-width: 767px) {
    font-size: 14px;
    margin-top: 15px;
  }
`

const NavDecoLine = styled(motion.div)`
  position: absolute;
  border-bottom: 4px solid var(--bs-indigo);
  width: 100%;
  bottom: -27px;
  transform-origin: center;
`

const Title = styled.div`
  text-align: center;
  font-weight: bold;
  margin: 40px 0 70px;
  font-size: 48px;
  @media (max-width: 1199px) {
    font-size: 42px;
  }
  @media (max-width: 991px) {
    font-size: 38px;
    margin: 35px 0 55px;
  }
  @media (max-width: 767px) {
    font-size: 35px;
    margin: 30px 0 40px;
  }
`

export const MenuContainer = ({
  title,
  menu,
  children,
  pointHeight,
  pointWidth,
  pointRadius,
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const companyItems = [
    { path: '/company/greetings', label: '인사말' },
    { path: '/company/history', label: '연혁' },
    { path: '/company/license', label: '면허보유현황' },
    { path: '/company/vision', label: '비전 및 미션' },
    { path: '/company/map', label: '찾아오시는 길' },
  ]

  const businessItems = [
    { path: '/business/facility', label: '시설개요' },
    { path: '/business/distribution', label: '물류 시설 현황' },
    { path: '/business/equipment', label: '장비 현황' },
  ]

  const selectedItems =
    title === '회사소개'
      ? companyItems
      : title === '사업소개'
      ? businessItems
      : []

  const toggleMenu = () => {
    setShowMenu((prev) => !prev)
    setIsVisible(true)
  }

  const imageSrc =
    title === '회사소개'
      ? '/sub-banner'
      : title === '사업소개'
      ? '/sj-side'
      : title === '갤러리'
      ? '/sjl-gallery'
      : ''

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowMenu(false)
        setIsVisible(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <>
      <div id="banner" className="image-container">
        <picture>
          <source srcSet={`${imageSrc}.webp`} type="image/webp" />
          <img
            src={`${imageSrc}.jpg`}
            alt={`${title} 배너 이미지`}
            className="zoom-out"
          />
        </picture>
        <div className="text-center text-white position-absolute z-1 zoom-in-center">
          <h1 className="fw-bolder">{title}</h1>
          <P>
            정직과 정도 경영으로
            <br />
            고객의 신뢰를 얻겠습니다.
          </P>
        </div>
      </div>
      {/* mini navbar */}
      <nav style={{ borderBottom: '0.5px solid #6C819C', height: '80px' }}>
        <Container className="d-flex justify-content-md-between">
          <div className="bg-primary d-flex align-items-center justify-content-center fw-bold sub-menu">
            {title}
          </div>
          {/* md 사이즈 이상에만 적용 */}
          <div className="d-none d-md-flex align-items-center position-relative">
            {selectedItems.map((item) => (
              <div key={item.path} style={{ position: 'relative' }}>
                <Nav.Link
                  as={NavLink}
                  to={item.path}
                  className={`mx-2 mx-lg-3`}
                >
                  {item.label}
                </Nav.Link>
                {/* NavDecoLine 추가 */}
                <NavDecoLine
                  initial={{ scaleX: 0 }}
                  animate={
                    item.path === window.location.pathname
                      ? { scaleX: 1 }
                      : { scaleX: 0 }
                  }
                  transition={{ duration: 0.3 }}
                />
              </div>
            ))}
          </div>
          {/* md 사이즈 미만 적용 */}
          <div className="d-md-none">
            <button
              className={`align-items-center justify-content-center d-flex sub-menu bg-white`}
              style={{
                border: '1px solid #6C819C',
                borderBottom: showMenu
                  ? '1px solid white'
                  : '1px solid #6C819C',
              }}
              onClick={toggleMenu}
            >
              <b className="text-primary">{menu}</b>
              <img src="/dropdown.svg" alt="dropdown icon" className="ms-2" />
            </button>

            <MenuWrapper show={showMenu} isVisible={isVisible}>
              <Nav className="flex-column">
                {selectedItems.map((item) => (
                  <Nav.Link
                    key={item.path}
                    as={NavLink}
                    to={item.path}
                    className="py-3"
                  >
                    {item.label}
                  </Nav.Link>
                ))}
              </Nav>
            </MenuWrapper>
          </div>
        </Container>
      </nav>
      <Container
        id="content"
        style={{
          marginTop: '108px',
          marginBottom: '160px',
        }}
      >
        <Point
          pintWidth={pointWidth}
          pointHeight={pointHeight}
          pointRadius={pointRadius}
        />
        <Title id="content-title">{menu}</Title>
        {children}
      </Container>
    </>
  )
}

// props type
MenuContainer.propTypes = {
  title: PropTypes.string.isRequired, // 사진 위에 뜨는 텍스트
  menu: PropTypes.string.isRequired, // 메뉴
  children: PropTypes.node.isRequired, // 안에 들어갈 내용
  pointWidth: PropTypes.number,
  pointHeight: PropTypes.number,
  pointRadius: PropTypes.string,
  pointMargin: PropTypes.string,
}
